import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FolderKanban, Layers } from 'lucide-react';
import { requireFeature } from '@/common/utils/requireFeature';
import { usePermissions } from '@/common/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { techSystemsService } from '@/features/tech-systems/services/tech-systems.service';
import { TechSystemsListFilters } from '@/features/tech-systems/components/TechSystemsListFilters';
import { TechSystemsGrid } from '@/features/tech-systems/components/TechSystemsGrid';
import {
  canAccessProjectsHub,
  canAccessTechSystemsModule,
} from '@/features/tech-systems/utils/projects-access';

export const Route = createFileRoute('/_authenticated/tech-systems/projects/')({
  beforeLoad: () => requireFeature('TECH_SYSTEMS'),
  component: TechSystemsProjectsHubPage,
});

function TechSystemsProjectsHubPage() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');

  const hasProjectsHubAccess = canAccessProjectsHub(hasPermission);
  const hasModuleAccess = canAccessTechSystemsModule(hasPermission);

  const { data: systems = [], isLoading } = useQuery({
    queryKey: ['tech-systems-projects-hub', search, statusFilter],
    queryFn: () =>
      techSystemsService.listForProjectsHub({
        ...(search && { search }),
        status: statusFilter,
      }),
    enabled: hasProjectsHubAccess && hasModuleAccess,
  });

  if (!hasProjectsHubAccess) {
    return (
      <div className="space-y-4 p-6">
        <h1 className="text-2xl font-semibold">Gestão de projetos</h1>
        <p className="text-sm text-muted-foreground">
          Você não tem permissão para acessar a gestão de projetos dos Sistemas
          TEC.
        </p>
      </div>
    );
  }

  if (!hasModuleAccess) {
    return (
      <div className="space-y-4 p-6">
        <h1 className="text-2xl font-semibold">Gestão de projetos</h1>
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Você possui acesso à gestão de projetos, mas não tem permissão no
            módulo Sistemas TEC. Solicite acesso a um gestor.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <FolderKanban className="h-7 w-7 text-primary" />
            Gestão de projetos
          </h1>
          <p className="text-sm text-muted-foreground">
            Selecione um sistema para gerenciar o cronograma e as tarefas do
            projeto
          </p>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to="/tech-systems/templates">
            <Layers className="h-4 w-4" />
            Templates de projeto
          </Link>
        </Button>
      </div>

      <TechSystemsListFilters
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        searchPlaceholder="Buscar sistemas com projetos..."
      />

      {isLoading ? (
        <p className="text-muted-foreground">Carregando sistemas...</p>
      ) : systems.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Nenhum sistema encontrado com os filtros atuais. Verifique se você
            tem acesso aos sistemas desejados.
          </CardContent>
        </Card>
      ) : (
        <TechSystemsGrid
          systems={systems}
          onSelect={(system) =>
            navigate({
              to: '/tech-systems/projects/$id',
              params: { id: system.id },
            })
          }
        />
      )}
    </div>
  );
}
